import { useMeasure } from "@uidotdev/usehooks";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import type { Vote } from "../../../types/voteValue";
import { BackfaceCard } from "../../BackfaceCard/BackfaceCard";
import { FlyingUpReactions } from "../../FlyingUpReactions/FlyingUpReactions";
import { FrontfaceCard } from "../../FrontfaceCard/FrontfaceCard";
import { PlaceholderCard } from "../../PlaceholderCard/PlaceholderCard";
import { TreeDimensionCard } from "../../TreeDimensionCard/TreeDimensionCard";
import { UserDisplay } from "../../UserDisplay/UserDisplay";
import type { ParticipantCardProps } from "./types";
import { useParticipantCard } from "./useParticipantCard";

export const ParticipantCard = ({
  online,
  participant,
}: ParticipantCardProps) => {
  const {
    actionShown,
    flyingUpReactions,
    handleNudgeButtonClick,
    nudged,
    participantProfile,
    revealed,
    setActionShown,
    setFlyingUpReactions,
    setNudged,
    setZooming,
    userParticipant,
    zooming,
  } = useParticipantCard({ participant });
  const [containerRef, { width: containerWidth }] = useMeasure();
  const { t } = useTranslation();

  if (!participantProfile) {
    return null;
  }

  return (
    <div
      className={classNames({ grayscale: !online })}
      data-testid="participant-card"
      ref={containerRef}
    >
      <div className="fixed" style={{ width: containerWidth ?? undefined }}>
        <FlyingUpReactions
          flyingUpReactions={flyingUpReactions}
          onFlyingUpReactionAnimationEnd={(flyingUpReactionId) =>
            setFlyingUpReactions((prev) =>
              prev.filter(({ id }) => id !== flyingUpReactionId),
            )
          }
        />
      </div>
      <div className="flex flex-col items-center gap-3 lg:gap-4">
        <div
          className="group/card-wrapper not-pointer-coarse:p-4 not-pointer-coarse:pb-0"
          onMouseEnter={() => setActionShown(true)}
          onMouseLeave={() => setActionShown(false)}
        >
          <div className="relative">
            <TreeDimensionCard
              backfaceCard={
                participant.vote ? (
                  <BackfaceCard
                    backfaceCardStyleKey={
                      participantProfile.backface_card_style_key
                    }
                    cursorDefault
                    inert={revealed}
                    onClick={() =>
                      setActionShown((actionShown) => !actionShown)
                    }
                    tagElement={userParticipant ? "button" : "div"}
                  />
                ) : (
                  <PlaceholderCard
                    cursorDefault
                    inert={revealed}
                    onClick={() =>
                      setActionShown((actionShown) => !actionShown)
                    }
                    tagElement={userParticipant ? "button" : "div"}
                  />
                )
              }
              className={classNames("w-24 lg:w-28 2xl:w-32", {
                "animate-shake": nudged,
                "animate-zoom": zooming,
              })}
              frontfaceCard={
                <FrontfaceCard
                  cursorDefault
                  inert={!revealed}
                  onClick={() => setActionShown((actionShown) => !actionShown)}
                  tagElement={userParticipant ? "button" : "div"}
                  voteValue={participant.vote as Vote}
                />
              }
              onAnimationEnd={() => {
                setNudged(false);
                setZooming(false);
              }}
              revealed={revealed}
            />
            {userParticipant && (
              <div
                className={classNames(
                  "absolute top-0 right-0 translate-x-1/4 -translate-y-1/4",
                  "transition-discrete duration-200",
                  {
                    "animate-in fade-in slide-in-from-top-5 slide-in-from-right-5 flex":
                      actionShown,
                    "animate-out fade-out slide-out-to-top-5 slide-out-to-right-5 hidden origin-bottom-left":
                      !actionShown,
                  },
                )}
              >
                <button
                  className="border-pill hover:shadow-pill bg-lemon-50 size-14 cursor-pointer rounded-full text-2xl dark:bg-zinc-900"
                  data-testid="participant-card-nudge-button"
                  onClick={handleNudgeButtonClick}
                  title={t("entities.nudge.actions.send_nudge")}
                >
                  🫨
                </button>
              </div>
            )}
          </div>
        </div>
        <UserDisplay online={online} profile={participantProfile} />
      </div>
    </div>
  );
};
